import type {
  FrequencyPeriod,
  IdentityMode,
  OperatingMode,
  PhilosophyPosture,
} from 'api/recoveryAi.types'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { DEFAULT_FREQUENT_PERIOD } from './steps/limits/periodFormat'
import { useWizardHydration } from './useWizardHydration'

export interface WizardState {
  // AI Identity
  assistantName: string
  // Nullable to match RecoveryConfig.identity_mode — staying null until the
  // user actually picks a radio option prevents the default `'ai_identified'`
  // from being persisted on behalf of a merchant who never touched the step.
  identityMode: IdentityMode | null
  customIntroduction: string

  // Behavior Mode
  operatingMode: OperatingMode
  // True once hydrated from an already-onboarded config (config.onboarding_completed).
  // Marks the wizard as a RE-ENTRY rather than first-time setup, so Finalize
  // preserves launch-time fields it has no UI for (operating_mode) instead of
  // clamping/overwriting them — a re-PUT must never silently change what mode a
  // live agent is already running in. Session-only; defaults false.
  alreadyOnboarded: boolean

  // Assessment
  assessmentAnswers: Record<number, number>
  recommendedPosture: PhilosophyPosture | null

  // Philosophy
  philosophyPosture: PhilosophyPosture
  // True once the merchant has explicitly picked a stance card. Lives in
  // provider state (not a per-step useRef) so it survives the philosophy step
  // unmounting/remounting on wizard navigation — otherwise a remount would
  // re-fire the auto-apply effect and clobber the user's choice with the
  // assessment recommendation. (AI-29 review.)
  philosophyPostureOverridden: boolean

  // Limits & Guardrails
  dailyBudget: string
  selectedRewardIds: string[]
  frequentThreshold: string
  frequencyPeriod: FrequencyPeriod
  repeatedThreshold: string
  // Escalation RECIPIENTS are deliberately absent (D1/L31): they live on the
  // escalation_contacts resource, which the Escalations step reads and writes
  // directly, so there is no wizard-local copy to drift from Settings. Same for
  // the location-managers toggle. notifyAdmins stays — it has no UI, and
  // Finalize must round-trip it rather than let the default overwrite a saved
  // false.
  notifyAdmins: boolean

  // Session-only (not persisted). Reserved: this flag formerly gated the
  // end-of-flow Processing/calibration screen, but AI-242 moved that interstitial
  // to run between Train and Testing (reachable once Train is complete via
  // isAssessmentComplete), so nothing reads or writes it today. Kept as a
  // reserved field (defaultState seeds it false) rather than removed, for a
  // surgical AI-242 diff.
  didFinalize: boolean

  // Session-only (not persisted — useWizardSubmit maps 'testing' to {}): set
  // true once the merchant completes the 3-persona guided playback in the
  // Testing stage, which unlocks that stage's Continue. Lives in provider state
  // (mounted above the :step route) so it survives navigating away from and
  // back to Testing within a session — same lifetime (and required-ness) as
  // didFinalize; defaultState seeds it false.
  testingComplete: boolean

  // Session-only (AI-255): per-VISIT "already shown" guards for the two Testing
  // coach tours. Provider-scoped so a tour doesn't re-pop as the merchant moves
  // between wizard steps within one visit, but resets when they leave /configure
  // (Cancel unmounts the provider) — so a merchant who has NOT finished setup
  // sees the tour again when they return to resume. Whether the tour shows at all
  // is gated on onboarding_completed (finished merchants never see it); these
  // flags only dedup within a single visit. NOT a permanent per-merchant flag.
  tourSeen: boolean
  tour2Seen: boolean

  // Session-only (not persisted by the config PUT): the Locations-stage
  // selection (AI-161). The stage persists the enable/disable choice via the
  // dedicated PUT recovery/location_configs — via the persist action LocationsStep
  // registers into continueActionRef below (AI-1130; there is no Save button
  // anymore) — NOT the config PUT, so these are NOT hydrated by
  // useWizardHydration (which reads the config). They live in provider state
  // (mounted above the :step route) to back the picker across renders within a
  // single stage visit.
  //
  // NOTE — these are NOT a durable cross-navigation draft. useWizardLocations
  // re-derives mode + selection from the SAVED per-location state on every stage
  // (re)mount (its hasHydratedRef is per-mount, and renderStep unmounts the step
  // on wizard navigation), so an UNSAVED edit is replaced by saved truth when the
  // merchant leaves and re-enters the stage. This is intentional and mirrors
  // ActiveLocationsPane (which re-reads on mount): the invariant is "what you see
  // on entering the stage equals what is persisted", traded against persisting an
  // uncommitted draft. `locationMode` mirrors ActiveLocationsPane's all-vs-subset
  // Mode; defaultState seeds 'all' + an empty selection.
  locationMode: 'all' | 'subset'
  selectedLocationIds: number[]
  // Session-only: true whenever the Locations stage has an edit (a mode switch,
  // a toggle, a remove) that has NOT yet been persisted. Synced from
  // useWizardLocations. AI-1130: Continue no longer gates on this — it now
  // persists the edit itself via continueActionRef before advancing. The sole
  // remaining consumer is the wizard-chrome's Back/Cancel "discard changes?"
  // confirm dialog (leaveWouldDiscardEdit in index.tsx) — leaving via Back/
  // Cancel deliberately does NOT persist, so that guard still needs this flag.
  // defaultState seeds false.
  locationsUnsaved: boolean
}

interface WizardContextValue {
  state: WizardState
  update: (partial: Partial<WizardState>) => void
  // AI-1130: lets the CURRENTLY MOUNTED step register an async action for the
  // wizard-chrome Continue button (index.tsx) to run before it advances — e.g.
  // LocationsStep's location enable/disable PUT, which cannot ride the generic
  // config-PUT submitStep path (see useWizardSubmit's 'locations' case). A ref,
  // not state: setting it must never re-render Configure, and Continue reads
  // `.current` synchronously at click time, not during render. `null` when the
  // mounted step has nothing for Continue to run.
  continueActionRef: React.MutableRefObject<(() => Promise<void>) | null>
}

export const defaultState: WizardState = {
  assistantName: '',
  identityMode: null,
  customIntroduction: '',
  // Never sent as-is: this is a pre-hydration placeholder meaning "assume already
  // running — do not re-arm" until useWizardHydration overwrites it with the real
  // saved mode. It is not a value the wizard chooses or persists.
  operatingMode: 'feedback_only',
  alreadyOnboarded: false,
  assessmentAnswers: {},
  recommendedPosture: null,
  philosophyPosture: 'generous',
  philosophyPostureOverridden: false,
  dailyBudget: '1000',
  selectedRewardIds: [],
  frequentThreshold: '3',
  frequencyPeriod: DEFAULT_FREQUENT_PERIOD,
  repeatedThreshold: '3',
  notifyAdmins: true,
  didFinalize: false,
  testingComplete: false,
  tourSeen: false,
  tour2Seen: false,
  locationMode: 'all',
  selectedLocationIds: [],
  locationsUnsaved: false,
}

const WizardContext = createContext<WizardContextValue>({
  state: defaultState,
  update: () => {},
  continueActionRef: { current: null },
})

export const useWizardState = () => useContext(WizardContext)

type Props = {
  children: React.ReactNode
}

export const WizardProvider = ({ children }: Props) => {
  const [state, setState] = useState<WizardState>(defaultState)
  const continueActionRef = useRef<(() => Promise<void>) | null>(null)

  const update = useCallback((partial: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...partial }))
  }, [])

  useWizardHydration(update)

  // AI-160 (review): testingComplete certifies the merchant tested the agent
  // they're about to activate, so it must NOT be a one-way latch — re-arm the
  // Testing-stage gate whenever an agent-shaping input changes after a completed
  // test (otherwise: complete Testing, go Back, change posture / voice / rewards
  // / budget, and walk forward past a gate that no longer reflects what was
  // tested). The updater is a no-op (returns the same object → no re-render)
  // unless currently complete, so normal forward editing is unaffected. Today the
  // pre-activation simulator reads the SAVED config rather than this in-progress
  // state, so this is belt-and-suspenders; it becomes load-bearing once the
  // draft-config cable (WP1/F3 — the CONDITIONAL AC) and AI-167's in-Testing
  // tuning let the merchant change the agent under test. Escalation routing
  // doesn't shape replies, so it is excluded — and since D1 it isn't wizard
  // state at all (the step writes the contacts resource directly).
  useEffect(() => {
    setState(prev =>
      prev.testingComplete ? { ...prev, testingComplete: false } : prev
    )
    // The listed slices are intentional change-triggers, not body references.
    // Brand voice was dropped from the deps in W7 — it's no longer wizard-local
    // state; re-add it the day the config modals mount in the Testing stage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.assessmentAnswers,
    state.philosophyPosture,
    state.selectedRewardIds,
    state.dailyBudget,
  ])

  const value = useMemo(
    () => ({ state, update, continueActionRef }),
    [state, update, continueActionRef]
  )

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  )
}

export default WizardContext
